import { useState, useCallback, useEffect } from "react";
import api, { fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function PatternEditor({ empId, employee, existing, onSaved }) {
  const [f, setF] = useState(() => existing ? {
    name: existing.name || "",
    effective_from: existing.effective_from || "",
    effective_to: existing.effective_to || "",
    cycle_length: existing.cycle_length || 1,
    anchor_date: existing.anchor_date || "",
    reason: existing.reason || "",
    week1_hours: existing.week1_hours || [7.5, 7.5, 7.5, 7.5, 7.5, 0, 0],
    week2_hours: existing.week2_hours || [7.5, 7.5, 7.5, 7.5, 7.5, 0, 0],
  } : {
    name: "", effective_from: "", effective_to: "", cycle_length: 1,
    anchor_date: "", reason: "",
    week1_hours: [7.5, 7.5, 7.5, 7.5, 7.5, 0, 0],
    week2_hours: [7.5, 7.5, 7.5, 7.5, 7.5, 0, 0],
  });
  const [preview, setPreview] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setHour = (week, idx, val) => setF((s) => {
    const arr = [...s[week]]; arr[idx] = val === "" ? 0 : parseFloat(val); return { ...s, [week]: arr };
  });

  const total = (arr) => arr.reduce((a, b) => a + (parseFloat(b) || 0), 0);

  const doPreview = useCallback(async () => {
    try {
      const payload = {
        name: f.name || "Preview", effective_from: f.effective_from || new Date().toISOString().slice(0, 10),
        effective_to: f.effective_to || null, cycle_length: Number(f.cycle_length),
        anchor_date: f.cycle_length == 2 ? (f.anchor_date || null) : null,
        week1_hours: f.week1_hours.map(Number), week2_hours: f.cycle_length == 2 ? f.week2_hours.map(Number) : null,
      };
      const { data } = await api.post("/patterns/preview", payload);
      setPreview(data);
    } catch (e) { setPreview(null); }
  }, [f]);

  useEffect(() => { doPreview(); }, [doPreview]);

  const save = async () => {
    try {
      const payload = {
        name: f.name, effective_from: f.effective_from, effective_to: f.effective_to || null,
        cycle_length: Number(f.cycle_length), anchor_date: f.cycle_length == 2 ? f.anchor_date : null,
        week1_hours: f.week1_hours.map(Number), week2_hours: f.cycle_length == 2 ? f.week2_hours.map(Number) : null,
        reason: f.reason,
      };
      const { data } = existing
        ? await api.put(`/patterns/${existing.id}`, payload)
        : await api.post(`/employees/${empId}/patterns`, payload);
      if (data.warnings?.length) setWarnings(data.warnings);
      if (data.affected_bookings?.length) toast.warning(`${data.affected_bookings.length} booking(s) recalculated`);
      toast.success(existing ? "Pattern updated" : "Pattern saved");
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.errors) { toast.error(d.errors.join("; ")); setWarnings(d.warnings || []); }
      else toast.error(formatApiError(d));
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-xs text-slate-500">Pattern name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="pattern-name-input" placeholder="e.g. Standard hours" /></div>
        <div>
          <Label className="text-xs text-slate-500">Cycle length</Label>
          <Select value={String(f.cycle_length)} onValueChange={(v) => set("cycle_length", Number(v))}>
            <SelectTrigger data-testid="working-pattern-cycle-toggle"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="1">One week</SelectItem><SelectItem value="2">Two weeks</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs text-slate-500">Effective from</Label><Input type="date" value={f.effective_from} onChange={(e) => set("effective_from", e.target.value)} data-testid="pattern-from-input" /></div>
        <div><Label className="text-xs text-slate-500">Effective to (optional)</Label><Input type="date" value={f.effective_to} onChange={(e) => set("effective_to", e.target.value)} /></div>
        {f.cycle_length == 2 && (
          <div><Label className="text-xs text-slate-500">Cycle anchor date (start of Week 1, a Monday)</Label><Input type="date" value={f.anchor_date} onChange={(e) => set("anchor_date", e.target.value)} data-testid="pattern-anchor-input" /></div>
        )}
        <div><Label className="text-xs text-slate-500">Reason for pattern / change</Label><Input value={f.reason} onChange={(e) => set("reason", e.target.value)} /></div>
      </div>

      {/* Hours grid */}
      <div className="bg-[#F8FAFC] border rounded-lg p-4">
        <HoursRow label="Week 1" days={f.week1_hours} onChange={(i, v) => setHour("week1_hours", i, v)} total={total(f.week1_hours)} testid="week1" />
        {f.cycle_length == 2 && <div className="mt-3"><HoursRow label="Week 2" days={f.week2_hours} onChange={(i, v) => setHour("week2_hours", i, v)} total={total(f.week2_hours)} testid="week2" /></div>}
      </div>

      {/* Live preview */}
      {preview && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="pattern-preview">
          <div className="flex flex-wrap gap-4 text-xs text-slate-600 mb-3">
            <span>Week 1: <b className="text-[#0B1A2F]">{preview.week1_total}h</b></span>
            {f.cycle_length == 2 && <><span>Week 2: <b className="text-[#0B1A2F]">{preview.week2_total}h</b></span><span>Cycle: <b className="text-[#0B1A2F]">{preview.cycle_total}h</b></span></>}
            <span>Avg weekly: <b className="text-[#2EA3FD]">{preview.avg_weekly_hours}h</b></span>
            <span>Avg days/wk: <b className="text-[#0B1A2F]">{preview.avg_working_days}</b></span>
          </div>
          <p className="text-xs font-semibold text-slate-500 mb-2">6-week calendar preview {f.cycle_length == 2 && "— confirm Week 1 / Week 2 alignment"}</p>
          <div className="space-y-1">
            <div className="grid grid-cols-8 gap-1 text-center text-[10px] text-slate-400">
              <div />{DAYS.map((d) => <div key={d}>{d}</div>)}
            </div>
            {preview.weeks.map((wk, i) => (
              <div key={i} className="grid grid-cols-8 gap-1 text-center text-xs items-center">
                <div className="text-[10px] text-slate-400">{fmtDate(wk[0].date).slice(0, 6)}</div>
                {wk.map((d) => (
                  <div key={d.date} className={`py-1 rounded ${d.hours > 0 ? (d.cycle_week === 1 ? "bg-[#EFF8FF] text-[#0B1A2F]" : "bg-indigo-50 text-indigo-700") : "bg-slate-50 text-slate-300"}`} title={`${d.weekday} · Week ${d.cycle_week}`}>{d.hours}</div>
                ))}
              </div>
            ))}
          </div>
          {f.cycle_length == 2 && <div className="flex gap-4 mt-2 text-[10px] text-slate-500"><span className="flex items-center gap-1"><span className="h-2 w-3 rounded bg-[#EFF8FF] border" />Week 1</span><span className="flex items-center gap-1"><span className="h-2 w-3 rounded bg-indigo-50 border" />Week 2</span></div>}
        </div>
      )}

      {warnings.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="pattern-warnings">⚠ {warnings.join(" · ")}</div>}

      <Button onClick={save} className="bg-[#2EA3FD] hover:bg-[#1B8EE6] w-full" data-testid="pattern-save-btn">{existing ? "Update pattern" : "Save pattern"}</Button>
    </div>
  );
}

function HoursRow({ label, days, onChange, total, testid }) {
  return (
    <div className="grid grid-cols-9 gap-2 items-center">
      <span className="text-xs font-semibold text-slate-500">{label}</span>
      {days.map((h, i) => (
        <div key={i} className="text-center">
          <span className="block text-[10px] text-slate-400 mb-0.5">{DAYS[i]}</span>
          <Input type="number" step="0.5" min="0" value={h} onChange={(e) => onChange(i, e.target.value)}
            data-testid={`${testid}-day-${i}`} className="text-center px-1 h-9 tabular" />
        </div>
      ))}
      <div className="text-center"><span className="block text-[10px] text-slate-400 mb-0.5">Total</span><span className="block h-9 leading-9 font-bold text-[#2EA3FD] tabular">{total}h</span></div>
    </div>
  );
}
